// src/services/analyticsV2Service.js
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';
import { formatDateLocal } from '../utils/formatters';

const ANALYTICS_V2_URL = `${NODE_BACKEND_API_URL}/analytics/v2`;

// Helper to get auth token from localStorage
const getAuthToken = () => {
    return localStorage.getItem('authToken');
};

// Helper to format date as YYYY-MM-DD
// Usa los componentes de calendario LOCAL, no toISOString().
// El backend trata endDate como INCLUSIVO (sp_GetProfitAnalysisSummary y los
// demás SP v2 agregan un día o comparan <= @EndDate), así que hay que enviar
// el último día del rango tal como lo ve el usuario. Con toISOString(), un
// cliente en Argentina (UTC-3) convertía "31/10 23:59:59.999" a "2026-11-01"
// y cada consulta "Mes Pasado" incluía además el 1° de noviembre.
export const formatDateForAPI = (date) => formatDateLocal(date);

/**
 * Calculate standard date ranges for analytics
 * @returns {Object} Date ranges with start/end dates and labels
 */
export const getDateRanges = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    // Today: 00:00:00 to 23:59:59 of current day
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    
    // Week: Monday 00:00:00 to today 23:59:59
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    
    // Current Month: 1st of month 00:00:00 to today 23:59:59
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    currentMonthStart.setHours(0, 0, 0, 0);
    
    // Previous Month: 1st to last day of previous month
    const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    prevMonthStart.setHours(0, 0, 0, 0);
    const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    prevMonthEnd.setHours(23, 59, 59, 999);
    
    return {
        today: {
            start: formatDateForAPI(todayStart),
            end: formatDateForAPI(today),
            label: 'Hoy'
        },
        week: {
            start: formatDateForAPI(monday),
            end: formatDateForAPI(today),
            label: 'Esta Semana'
        },
        currentMonth: {
            start: formatDateForAPI(currentMonthStart),
            end: formatDateForAPI(today),
            label: 'Este Mes'
        },
        prevMonth: {
            start: formatDateForAPI(prevMonthStart),
            end: formatDateForAPI(prevMonthEnd),
            label: 'Mes Pasado'
        }
    };
};

/**
 * Analytics V2 Service - Date Range Based Endpoints
 * These endpoints accept explicit startDate and endDate parameters
 * ensuring consistent data across all dashboard components
 */
const analyticsV2Service = {
    /**
     * Get sales summary for a specific date range
     * @param {string} startDate - Start date in YYYY-MM-DD format
     * @param {string} endDate - End date in YYYY-MM-DD format
     * @returns {Promise} Sales summary with invoice count, total sales, and average ticket
     */
    getSalesSummary: async (startDate, endDate) => {
        try {
            const response = await axios.get(`${ANALYTICS_V2_URL}/sales-summary`, {
                params: { startDate, endDate },
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            console.error('[Analytics V2] Error getting sales summary:', error);
            throw error;
        }
    },

    /**
     * Get sales by category for a specific date range
     * @param {string} startDate - Start date in YYYY-MM-DD format
     * @param {string} endDate - End date in YYYY-MM-DD format
     * @returns {Promise} Sales breakdown by category with totals and percentages
     */
    getSalesByCategory: async (startDate, endDate) => {
        try {
            const response = await axios.get(`${ANALYTICS_V2_URL}/sales-by-category`, {
                params: { startDate, endDate },
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            console.error('[Analytics V2] Error getting sales by category:', error);
            throw error;
        }
    },

    /**
     * Get payment methods distribution for a specific date range
     * @param {string} startDate - Start date in YYYY-MM-DD format
     * @param {string} endDate - End date in YYYY-MM-DD format
     * @returns {Promise} Payment methods breakdown with totals and percentages
     */
    getPaymentMethods: async (startDate, endDate) => {
        try {
            const response = await axios.get(`${ANALYTICS_V2_URL}/payment-methods`, {
                params: { startDate, endDate },
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            console.error('[Analytics V2] Error getting payment methods:', error);
            throw error;
        }
    },

    /**
     * Convenience method: Get all dashboard data for a period
     * Fetches sales summary, category breakdown, and payment methods in parallel
     * @param {string} startDate - Start date in YYYY-MM-DD format
     * @param {string} endDate - End date in YYYY-MM-DD format
     * @returns {Promise} Object containing summary, categories, and paymentMethods
     */
    getDashboardData: async (startDate, endDate) => {
        try {
            const [summary, categories, paymentMethods] = await Promise.all([
                analyticsV2Service.getSalesSummary(startDate, endDate),
                analyticsV2Service.getSalesByCategory(startDate, endDate),
                analyticsV2Service.getPaymentMethods(startDate, endDate)
            ]);

            return {
                summary: summary.data,
                categories: categories.data,
                paymentMethods: paymentMethods.data
            };
        } catch (error) {
            console.error('[Analytics V2] Error getting dashboard data:', error);
            throw error;
        }
    },

    /**
     * Get daily sales progress with 7-day average comparison
     * Provides statistically reliable comparison, projections, and progress tracking
     * @returns {Promise} Daily sales progress data with current sales, 7-day average, projections, and goals
     */
    getDailySalesProgress: async () => {
        try {
            const response = await axios.get(`${ANALYTICS_V2_URL}/daily-sales-progress`, {
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            console.error('[Analytics V2] Error getting daily sales progress:', error);
            throw error;
        }
    },

    /**
     * Get profit analysis for a specific date range
     * Provides detailed revenue and profit breakdowns by invoice type (Factura A, B, Presupuesto)
     * All monetary values WITHOUT IVA, with IVA collected shown separately
     * @param {string} startDate - Start date in YYYY-MM-DD format
     * @param {string} endDate - End date in YYYY-MM-DD format
     * @returns {Promise} Profit analysis with summary, invoice details, and product aggregation
     */
    getProfitAnalysis: async (startDate, endDate) => {
        try {
            const response = await axios.get(`${ANALYTICS_V2_URL}/profit-analysis`, {
                params: { startDate, endDate },
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            console.error('[Analytics V2] Error getting profit analysis:', error);
            throw error;
        }
    }
};

export default analyticsV2Service;
